import { useState } from 'react';
import { fetchSSOConfig } from '../../../api/sso';
import { useDispatch, useSelector } from 'react-redux';
import { clearAuthError, doPreLogin, authError } from '../../../actions/authActions';
import { extractApiErrorMessage } from '../../../util/error';
import {
  EmailInput,
  Error,
  FormGroup,
  GeneralError,
  Header,
  HighlightText,
  LoginButton,
  LoginForm,
  LoginFormMainElements,
  Subtitle,
  Title,
} from './PreLoginPage.styles';
import debounce from 'lodash/debounce';
import { Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import { z } from 'zod';
import AuthLayout from '../../AuthLayout/AuthLayout';
import { getAuthError, getPreLoginEmail } from '../../../selectors/auth';
import * as AuthServiceModule from '@asurion-core/frontend-utils/lib/AuthService';
import environment from '../../../environment';

const PreLoginPageSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
});

const PreLoginPage = () => {
  const dispatch = useDispatch();
  const [redirect, setRedirect] = useState(null);
  const history = useHistory();
  const currentAuthError = useSelector(getAuthError);

  const preLoginEmail = useSelector(getPreLoginEmail);

  async function doLogin(email) {
    try {
      const ssoConfig = await fetchSSOConfig(email);
      dispatch(doPreLogin(email, ssoConfig));

      if (!ssoConfig) {
        setRedirect('/sign-up-options');

        return;
      }

      if (ssoConfig?.oidcConfig && ssoConfig.enableSSO) {
        setRedirect('/sso-login');

        return;
      }

      if (ssoConfig?.userType === AuthServiceModule.USER_TYPE.PARTNER) {
        window.location.href = `${environment.businessLoginPage}?pre-login-email=${encodeURIComponent(email)}`;

        return;
      }

      setRedirect('/login');
    } catch (error) {
      // Extract error message from response using utility function
      const errorMessage = extractApiErrorMessage(error);

      // Dispatch to auth error instead of local state
      dispatch(authError(errorMessage));
    }
  }

  const loginDebounced = debounce(doLogin, 500);

  if (redirect) {
    history.push(redirect);
  }

  return (
    <AuthLayout>
      <Header>
        <Title>
          Customer service made <HighlightText>easy and affordable</HighlightText>
        </Title>
        <Subtitle>Combining humans and machine learning to create innovative customer support solutions.</Subtitle>
      </Header>
      <Formik
        initialValues={{
          email: preLoginEmail || '',
        }}
        // NOTE(pavlozaporozkyi|2024-10-15): Using custom validate here
        // instead of validationSchema={toFormikValidationSchema(PreLoginPageSchema)}
        // because of https://github.com/robertLichtnow/zod-formik-adapter/issues/28
        validate={(values) => {
          try {
            PreLoginPageSchema.parse(values);
          } catch (err) {
            return err.issues.reduce((acc, error) => {
              acc[error.path[0]] ??= error.message;

              return acc;
            }, {});
          }
        }}
        onSubmit={(values) => {
          dispatch(clearAuthError());
          loginDebounced(values?.email);
        }}
      >
        {() => (
          <LoginForm>
            <LoginFormMainElements>
              <FormGroup>
                <EmailInput type="email" name="email" placeholder="Email" />
                <Error name="email" component="div" />
              </FormGroup>
              {currentAuthError && <GeneralError className="general-error">{currentAuthError}</GeneralError>}
            </LoginFormMainElements>
            <LoginButton type="submit">Log In</LoginButton>
          </LoginForm>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default PreLoginPage;
